<template>
  <div>
    <canvas
      ref="canvas"
      style="position: absolute; pointer-events: none;"
    >
    </canvas>
  </div>
</template>

<script>
import { usePodLabelStore } from '@/stores/PodLabelStore.js';
import { usePlayerStore } from '@/stores/PlayerStore.js';

export default {
  name: "AWSLabelsLayer",
  data() {
    return {
      width: 0,
      height: 0,
      videoObserver: null,
      oldParent: null,
      filteredLabels: [], // Hold filtered labels
      playerCurrentTime: 0
    };
  },

  mounted() {
    // Set up video observer
    this.videoObserver = new ResizeObserver(this.onVideoResize);
    const videoElement = this.getVideoElement();
    if (videoElement) {
      this.videoObserver.observe(videoElement);

      // Store old parent and move canvas to video container
      this.oldParent = this.getCanvas().parentNode;
      const newParent = videoElement.parentNode;
      newParent.appendChild(this.getCanvas());

      // Set initial size
      this.width = videoElement.offsetWidth;
      this.height = videoElement.offsetHeight;
      this.getCanvas().width = this.width;
      this.getCanvas().height = this.height;

      // Initial draw
      this.drawObjects();
    }
  },

  beforeUnmount() {
    if (this.videoObserver) {
      this.videoObserver.disconnect();
    }
    if (this.oldParent && this.getCanvas()) {
      this.oldParent.appendChild(this.getCanvas());
    }
  },

  methods: {
    getCanvas() {
      return this.$refs.canvas;
    },

    getVideoElement() {
      const canvas = this.getCanvas();
      return canvas?.parentNode?.parentNode?.querySelector('video') || null;
    },

    onVideoResize(entries) {
      const canvas = this.getCanvas();
      if (!canvas || !entries.length) return;

      const { width, height } = entries[0].contentRect;
      canvas.width = width;
      canvas.height = height;
      this.width = width;
      this.height = height;

      this.drawObjects();
    },

    isWithinTimeRange(start, duration = 1) {
      const startSeconds = Math.round(start / 1000);
      return this.playerCurrentTime >= startSeconds && this.playerCurrentTime <= (startSeconds + duration);
    },

    drawObjects() {
      const canvas = this.getCanvas();
      if (!canvas) return;

      const context = canvas.getContext('2d');
      context.clearRect(0, 0, this.width, this.height);

      // Early return if no labels or dimensions
      if (!this.filteredLabels?.length || !this.width || !this.height) return;

      for (const label of this.filteredLabels) {
        const labelStart = label.start || 0;
        const labelEnd = label.end || 0;
        const duration = Math.round(labelEnd / 1000) - Math.round(labelStart / 1000);

        if (!this.isWithinTimeRange(labelStart, duration)) continue;

        for (const instance of (label.instances || [])) {
          const instanceStart = instance.Timestamp || 0;
          if (!this.isWithinTimeRange(instanceStart)) continue;

          if (this.showingRectangles) {
            this.drawRectangle(context, instance);
          }

          if (this.showingLabels) {
            this.drawTextLabel(context, label.content, instance);
          }
        }
      }
    },

    drawRectangle(context, instance) {
      if (!instance?.BoundingBox) return;

      const { Top, Left, Width, Height } = instance.BoundingBox;
      const top = Top * this.height;
      const left = Left * this.width;
      const width = Width * this.width;
      const height = Height * this.height;

      context.beginPath();
      context.strokeStyle = "#3dabff";
      context.lineWidth = 2;
      context.rect(left, top, width, height);
      context.stroke();
    },

    drawTextLabel(context, content, instance) {
      if (!instance?.BoundingBox || !content) return;

      const top = (instance.BoundingBox.Top * this.height) - 5;
      const left = instance.BoundingBox.Left * this.width;

      context.font = '14px Arial';
      context.fillStyle = "#3dabff";
      context.fillText(content, left, top);
    }
  },

  computed: {
    getMlLabels() {
      const podLabelStore = usePodLabelStore();
      const mlLabels =  podLabelStore.getMlLabels || [];

      return mlLabels.filter(x =>
          x.service === 'aws-detecting-labels' &&
          Array.isArray(x.instances) &&
          x.instances.length > 0
      );
    },

    showingLabels() {
      const playerStore = usePlayerStore();
      const options = playerStore.getLayersOptions?.AWSDETECTEDLABELS;

      return typeof options?.showLabelsOnLayer === 'boolean' ? options.showLabelsOnLayer : true;
    },

    showingRectangles() {
      const playerStore = usePlayerStore();
      const options = playerStore.getLayersOptions?.AWSDETECTEDLABELS;
      return typeof options?.showRectOnLayer === 'boolean' ? options.showRectOnLayer : true;
    }
  },

  watch: {
    getMlLabels: {
      immediate: true,
      handler(newLabels) {
        const playerStore = usePlayerStore();
        const confidenceLevel = playerStore.getLayersOptions?.AWSDETECTEDLABELS?.confidenceLevel || 90;

        this.filteredLabels = newLabels.filter(x => x.confidence >= confidenceLevel);
        this.drawObjects();
      },
      showingLabels: {
        immediate: true,
        handler() {
          this.drawObjects();
        }
      },
    },

    playerCurrentTime: {
      immediate: true,
      handler(newTime) {
        this.playerCurrentTime = newTime;
        this.drawObjects();
      }
    }
  }
};
</script>

<style>

</style>
